import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrandInfo, SeoAudit } from '@agency-smm/shared';
import { apiClient } from '../../services/apiClient';
import { Button } from '../Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../Card';
import { Input, InputWrapper, Label } from '../Input';
import { useBrandContext } from './brandShared';
import { BrandOverviewGlance } from './BrandOverviewGlance';
import { SeoAuditCard } from './SeoAuditCard';
import { AiLoadingOverlay } from '../AiLoadingOverlay';
import {
  Stack,
  ChecklistCard,
  ChecklistTitle,
  ChecklistSubtitle,
  ChecklistProgressBar,
  ChecklistProgressFill,
  ChecklistSteps,
  ChecklistStep,
  ChecklistIcon,
  ChecklistStepText,
  ChecklistStepTitle,
  ChecklistStepDesc,
  OrDivider,
  FileInput,
  FileInputLabel,
  SelectedFile,
  ClearFileButton,
  AutoFillErrorText,
  AutoFillBanner,
  AutoFillText,
  AutoFillTitle,
  AutoFillDesc,
  AutoFillActions,
  ScrapedImagesSection,
  ScrapedImagesHeader,
  ScrapedImagesTitle,
  SelectionInfo,
  ScrapedImagesGrid,
  ScrapedImageItem,
  SelectBadge,
  ScrapedActions,
  HelpText,
  ShortcutRow,
  DangerCard,
  DangerRow,
  DangerText,
} from './overviewStyled';

// Respuesta del scrape: BrandInfo recién insertado + imágenes encontradas +
// audit SEO/AI-readiness del sitio.
interface ScrapeResponse extends BrandInfo {
  scrapedImages?: string[];
  seoAudit?: SeoAudit | null;
}

/**
 * Tab "Overview" de la página de marca unificada:
 *  - Getting-Started checklist (3 pasos, barra de progreso, auto-oculta al
 *    completar, pasos clickeables que navegan / enfocan).
 *  - Auto-fill: "Analyze website" (scrape) + "Upload document" (analyze-document).
 *    Ambos insertan una fila brand_info → mergeamos al form y recargamos
 *    workspace/assets para que el usuario edite datos frescos.
 *  - Grid de imágenes scrapeadas → "Add as assets" / "Add as backgrounds" /
 *    "Dismiss" (las rotas se ocultan solas).
 *  - Shortcuts de workspace (Templates / Generate / Quick Generate).
 *  - Danger Zone (borrar workspace, solo owner).
 */
export function BrandTabOverview() {
  const navigate = useNavigate();
  const { setActiveTab, workspaceId, workspace, isOwner, form, assets, backgrounds, reloadWorkspace, openDeleteModal } =
    useBrandContext();

  // Estado local del auto-fill (no vive en el form: es flujo one-shot).
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [isScraping, setIsScraping] = useState(false);
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [isAnalyzingDocument, setIsAnalyzingDocument] = useState(false);
  const [autoFillError, setAutoFillError] = useState('');
  const [justAutoFilled, setJustAutoFilled] = useState(false);

  // Audit SEO fresco del último scrape de esta sesión (SeoAuditCard carga el
  // persistido por su cuenta; este lo pisa apenas llega).
  const [freshSeoAudit, setFreshSeoAudit] = useState<SeoAudit | null>(null);

  // Imágenes scrapeadas (selección + ocultar las rotas).
  const [scrapedImages, setScrapedImages] = useState<string[]>([]);
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set());
  const [isAddingAssets, setIsAddingAssets] = useState(false);

  // ─── Checklist (deriva del workspace + assets + form) ──────────────────────
  const hasBrandInfo = !!(form.formData.description || form.formData.businessOverview);
  const hasAssets = assets.assets.length > 0 || assets.assets.some((a) => a.isLogo);
  const hasPosts = (workspace?.postsCount || 0) > 0;
  const completedSteps = [hasBrandInfo, hasAssets, hasPosts].filter(Boolean).length;
  const totalSteps = 3;
  const allDone = completedSteps === totalSteps;

  // Tras un scrape/doc exitoso: mergea la fila insertada y refresca contexto.
  const applyInsertedBrandInfo = async (data: BrandInfo) => {
    form.mergeBrandInfo(data);
    setJustAutoFilled(true);
    // Recarga workspace (postsCount/logo) + assets para que el checklist avance.
    await Promise.all([reloadWorkspace(), assets.fetchAssets()]);
  };

  const handleScrape = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!scrapeUrl || !workspaceId) return;
    setIsScraping(true);
    setAutoFillError('');
    setScrapedImages([]);
    setSelectedImages(new Set());
    setBrokenImages(new Set());
    try {
      const data = await apiClient.post<ScrapeResponse>(`/api/workspaces/${workspaceId}/scrape`, {
        url: scrapeUrl,
      });
      await applyInsertedBrandInfo(data);
      setScrapeUrl('');
      if (data.scrapedImages && data.scrapedImages.length > 0) {
        setScrapedImages(data.scrapedImages);
      }
      if (data.seoAudit) setFreshSeoAudit(data.seoAudit);
    } catch (err) {
      setAutoFillError(err instanceof Error ? err.message : 'Failed to analyze website');
    } finally {
      setIsScraping(false);
    }
  };

  const handleDocumentUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!documentFile || !workspaceId) return;
    setIsAnalyzingDocument(true);
    setAutoFillError('');
    try {
      const fd = new FormData();
      fd.append('file', documentFile);
      const data = await apiClient.upload<BrandInfo>(
        `/api/workspaces/${workspaceId}/analyze-document`,
        fd,
      );
      await applyInsertedBrandInfo(data);
      setDocumentFile(null);
    } catch (err) {
      setAutoFillError(err instanceof Error ? err.message : 'Failed to analyze document');
    } finally {
      setIsAnalyzingDocument(false);
    }
  };

  // ─── Selección de imágenes scrapeadas ──────────────────────────────────────
  const toggleImageSelection = (imageUrl: string) => {
    setSelectedImages((prev) => {
      const next = new Set(prev);
      if (next.has(imageUrl)) next.delete(imageUrl);
      else next.add(imageUrl);
      return next;
    });
  };

  const dismissScraped = () => {
    setScrapedImages([]);
    setSelectedImages(new Set());
    setBrokenImages(new Set());
  };

  const handleAddAsAssets = async () => {
    if (selectedImages.size === 0) return;
    setIsAddingAssets(true);
    try {
      await assets.handleAddSelectedImages(selectedImages);
      dismissScraped();
    } catch (err) {
      console.error('Failed to add selected images as assets:', err);
    } finally {
      setIsAddingAssets(false);
    }
  };

  const handleAddAsBackgrounds = async () => {
    if (selectedImages.size === 0) return;
    await backgrounds.handleAddSelectedAsBackgrounds(selectedImages, dismissScraped);
  };

  // Solo mostramos las imágenes que no se rompieron al cargar.
  const visibleScraped = scrapedImages.filter((url) => !brokenImages.has(url));
  const isAddingBusy = isAddingAssets || backgrounds.isAddingSelectedAsBackgrounds;

  return (
    <Stack>
      {/* Pantalla de carga animada mientras la IA analiza la web/documento. */}
      <AiLoadingOverlay
        open={isScraping || isAnalyzingDocument}
        kicker="AgencySMM"
        title="Analyzing your brand…"
        subtitle={isAnalyzingDocument
          ? 'Reading your document and extracting its brand signals.'
          : 'Crawling the site and reading its code: color, type and voice.'}
        steps={[
          'Reading your sources',
          'Extracting colors & fonts',
          'Detecting voice & tone',
          'Structuring your brand profile',
          'Finishing up',
        ]}
      />

      {/* Brand at a glance — % de completitud + snapshot + qué falta. */}
      <BrandOverviewGlance />

      {/* Getting-Started checklist — se auto-oculta cuando los 3 pasos están listos. */}
      {!allDone && (
        <ChecklistCard>
          <ChecklistTitle>Getting Started</ChecklistTitle>
          <ChecklistSubtitle>Complete these steps to generate your first content</ChecklistSubtitle>
          <ChecklistProgressBar>
            <ChecklistProgressFill $progress={(completedSteps / totalSteps) * 100} />
          </ChecklistProgressBar>
          <ChecklistSteps>
            <ChecklistStep
              type="button"
              $done={hasBrandInfo}
              $clickable={!hasBrandInfo}
              onClick={() => {
                if (!hasBrandInfo) document.getElementById('scrapeUrl')?.focus();
              }}
            >
              <ChecklistIcon $done={hasBrandInfo}>
                {hasBrandInfo ? <span aria-hidden="true">✓</span> : '1'}
              </ChecklistIcon>
              <ChecklistStepText>
                <ChecklistStepTitle $done={hasBrandInfo}>Analyze your brand</ChecklistStepTitle>
                <ChecklistStepDesc>Paste a website URL or upload a document below</ChecklistStepDesc>
              </ChecklistStepText>
            </ChecklistStep>

            {/* Paso 2: los assets se gestionan en la tab "Assets". Antes era
                solo guía; ahora salta a esa tab vía setActiveTab del contexto. */}
            <ChecklistStep
              type="button"
              $done={hasAssets}
              $clickable={!hasAssets}
              onClick={() => {
                if (!hasAssets) setActiveTab('assets');
              }}
            >
              <ChecklistIcon $done={hasAssets}>
                {hasAssets ? <span aria-hidden="true">✓</span> : '2'}
              </ChecklistIcon>
              <ChecklistStepText>
                <ChecklistStepTitle $done={hasAssets}>Add brand assets</ChecklistStepTitle>
                <ChecklistStepDesc>Open the Assets tab to upload your logo and images</ChecklistStepDesc>
              </ChecklistStepText>
            </ChecklistStep>

            <ChecklistStep
              type="button"
              $done={hasPosts}
              $clickable={hasBrandInfo && !hasPosts}
              onClick={() => {
                if (hasBrandInfo && !hasPosts && workspaceId) {
                  navigate(`/workspace/${workspaceId}/generate`);
                }
              }}
            >
              <ChecklistIcon $done={hasPosts}>
                {hasPosts ? <span aria-hidden="true">✓</span> : '3'}
              </ChecklistIcon>
              <ChecklistStepText>
                <ChecklistStepTitle $done={hasPosts}>Generate your first posts</ChecklistStepTitle>
                <ChecklistStepDesc>Create AI-powered content for social media</ChecklistStepDesc>
              </ChecklistStepText>
            </ChecklistStep>
          </ChecklistSteps>
        </ChecklistCard>
      )}

      {/* Auto-fill — scrape de sitio o análisis de documento. Ambos insertan una
          fila brand_info; al volver mergeamos al form para edición fresca. */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Auto-fill your brand</CardTitle>
            <CardDescription>Analyze a website or a document to populate your brand profile</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleScrape}>
            <InputWrapper>
              <Label htmlFor="scrapeUrl">Website URL</Label>
              <Input
                id="scrapeUrl"
                type="url"
                value={scrapeUrl}
                onChange={(e) => setScrapeUrl(e.target.value)}
                placeholder="https://example.com"
              />
            </InputWrapper>
            <Button type="submit" $fullWidth disabled={isScraping || !scrapeUrl} style={{ marginTop: 16 }}>
              {isScraping ? 'Analyzing…' : 'Analyze Website'}
            </Button>
          </form>

          <OrDivider>or upload a document</OrDivider>

          <form onSubmit={handleDocumentUpload}>
            <FileInput
              type="file"
              id="documentFile"
              accept=".txt,.docx,.pdf"
              onChange={(e) => setDocumentFile(e.target.files?.[0] || null)}
            />
            {!documentFile ? (
              <FileInputLabel htmlFor="documentFile">Click to upload TXT, DOCX, or PDF</FileInputLabel>
            ) : (
              <SelectedFile>
                <span>{documentFile.name}</span>
                <ClearFileButton
                  type="button"
                  aria-label={`Remove ${documentFile.name}`}
                  onClick={() => setDocumentFile(null)}
                >
                  <span aria-hidden="true">×</span>
                </ClearFileButton>
              </SelectedFile>
            )}
            <Button
              type="submit"
              $fullWidth
              disabled={isAnalyzingDocument || !documentFile}
              style={{ marginTop: 16 }}
            >
              {isAnalyzingDocument ? 'Analyzing…' : 'Analyze Document'}
            </Button>
          </form>

          {autoFillError && <AutoFillErrorText>{autoFillError}</AutoFillErrorText>}

          {/* Nudge tras un auto-fill exitoso — los datos ya están en el form.
              El CTA salta a la tab Identity (setActiveTab) para revisarlos. */}
          {justAutoFilled && (
            <AutoFillBanner>
              <AutoFillText>
                <AutoFillTitle>Brand profile auto-populated</AutoFillTitle>
                <AutoFillDesc>
                  We&apos;ve filled in your brand profile from this analysis. Open the Identity tab to
                  review and refine it, then hit Save.
                </AutoFillDesc>
              </AutoFillText>
              <AutoFillActions>
                <Button
                  $size="sm"
                  onClick={() => {
                    setJustAutoFilled(false);
                    setActiveTab('identity');
                  }}
                >
                  Review in Identity
                </Button>
                <Button $size="sm" $variant="outline" onClick={() => setJustAutoFilled(false)}>
                  Got it
                </Button>
              </AutoFillActions>
            </AutoFillBanner>
          )}

          {/* Imágenes encontradas en el sitio — seleccionables. */}
          {visibleScraped.length > 0 && (
            <ScrapedImagesSection>
              <ScrapedImagesHeader>
                <ScrapedImagesTitle>Images Found on Website</ScrapedImagesTitle>
                <SelectionInfo>{selectedImages.size} selected</SelectionInfo>
              </ScrapedImagesHeader>
              <HelpText>Click images to select them, then add as brand assets or backgrounds.</HelpText>
              <ScrapedImagesGrid>
                {visibleScraped.map((imageUrl, index) => {
                  const isSelected = selectedImages.has(imageUrl);
                  return (
                    <ScrapedImageItem
                      key={imageUrl}
                      type="button"
                      $selected={isSelected}
                      aria-pressed={isSelected}
                      aria-label={`${isSelected ? 'Deselect' : 'Select'} scraped image ${index + 1}`}
                      onClick={() => toggleImageSelection(imageUrl)}
                    >
                      <img
                        src={imageUrl}
                        alt={`Scraped ${index + 1}`}
                        onError={() => setBrokenImages((prev) => new Set(prev).add(imageUrl))}
                      />
                      {isSelected && (
                        <SelectBadge>
                          <span aria-hidden="true">✓</span>
                        </SelectBadge>
                      )}
                    </ScrapedImageItem>
                  );
                })}
              </ScrapedImagesGrid>
              <ScrapedActions>
                <Button onClick={handleAddAsAssets} disabled={selectedImages.size === 0 || isAddingBusy}>
                  {isAddingAssets
                    ? 'Adding…'
                    : `Add as Asset${selectedImages.size !== 1 ? 's' : ''}`}
                </Button>
                <Button
                  $variant="outline"
                  onClick={handleAddAsBackgrounds}
                  disabled={selectedImages.size === 0 || isAddingBusy}
                >
                  {backgrounds.isAddingSelectedAsBackgrounds
                    ? 'Adding…'
                    : `Add as Background${selectedImages.size !== 1 ? 's' : ''}`}
                </Button>
                <Button $variant="ghost" onClick={dismissScraped}>
                  Dismiss
                </Button>
              </ScrapedActions>
            </ScrapedImagesSection>
          )}
        </CardContent>
      </Card>

      {/* Checklist semáforo de SEO / AI-readiness del sitio (último scrape). */}
      <SeoAuditCard workspaceId={workspaceId} freshAudit={freshSeoAudit} />

      {/* Shortcuts de navegación del workspace. */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Workspace shortcuts</CardTitle>
            <CardDescription>Jump straight to the work surfaces for this brand</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <ShortcutRow>
            <Button
              $variant="outline"
              onClick={() => workspaceId && navigate(`/workspace/${workspaceId}/templates`)}
            >
              Templates
            </Button>
            <Button onClick={() => workspaceId && navigate(`/workspace/${workspaceId}/generate`)}>
              Generate Content
            </Button>
            <Button
              $variant="outline"
              onClick={() => workspaceId && navigate(`/workspace/${workspaceId}/generate?quickgen=true`)}
              title="Generate posts per platform with smart defaults"
            >
              Quick Generate
            </Button>
          </ShortcutRow>
        </CardContent>
      </Card>

      {/* Danger zone — borrado del workspace (solo el dueño). */}
      {isOwner && (
        <DangerCard>
          <CardHeader>
            <div>
              <CardTitle>Danger Zone</CardTitle>
              <CardDescription>Irreversible actions for this workspace</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <DangerRow>
              <DangerText>
                <AutoFillTitle>Delete this workspace</AutoFillTitle>
                <AutoFillDesc>
                  Permanently removes all brand info, assets, generated posts, and export history. This
                  cannot be undone.
                </AutoFillDesc>
              </DangerText>
              <Button $variant="danger" onClick={openDeleteModal}>
                Delete workspace
              </Button>
            </DangerRow>
          </CardContent>
        </DangerCard>
      )}
    </Stack>
  );
}
